import type {HTTPHeaders, InitOverrideFunction} from '@linx-security/api-specs';
import {JSONApiResponse, PublicApi} from '@linx-security/api-specs';
import {HTTPMethod, createQueryParameters} from '@linx-security/api';

import {routes} from '../../_lib/browser/api-routes';

export class ConsoleApi extends PublicApi {
  /**
   * Dynamic request
   */
  async get<TParams extends unknown = unknown, TReturn extends object = object>(
    {path, requestParameters}: {path: string; requestParameters?: TParams},
    initOverrides?: RequestInit | InitOverrideFunction,
  ) {
    const queryParameters: any = createQueryParameters((requestParameters ?? {}) as any);

    const headerParameters: HTTPHeaders = {};

    const response = await this.request(
      {
        path,
        method: HTTPMethod.GET,
        headers: headerParameters,
        query: queryParameters,
      },
      initOverrides,
    );

    const json = new JSONApiResponse(response);
    return json.value() as Promise<TReturn>;
  }

  /**
   * Export turn table to Excel
   * Returns a blob with the Excel file and filename from Content-Disposition header
   *
   * IMPORTANT: This method bypasses the normal API middleware to preserve binary data integrity.
   * The middleware tries to parse responses as JSON which corrupts binary Excel files.
   */
  async publicExportTurnTable(requestParameters: {
    session_id: string;
    turn_id: string;
    table_index?: number;
  }): Promise<{blob: Blob; filename: string}> {
    // Build URL using the routes configuration (handles BASE_URL and proper encoding)
    const baseUrl = routes.aiAssistant.exportTurnTable({
      sessionId: requestParameters.session_id,
      turnId: requestParameters.turn_id,
    });

    // Build query string
    const queryParams = new URLSearchParams();
    if (requestParameters.table_index !== undefined) {
      queryParams.set('table_index', String(requestParameters.table_index));
    }
    const queryString = queryParams.toString();
    const url = `${baseUrl}${queryString ? `?${queryString}` : ''}`;

    // Make raw fetch call to avoid middleware that corrupts binary data
    // credentials: 'include' ensures session cookies are sent
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        // Don't set Content-Type for binary responses
        // The browser will handle it correctly
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Export failed: ${response.status} ${errorText}`);
    }

    // Get the blob directly - this preserves binary data integrity
    const blob = await response.blob();

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `table-${requestParameters.turn_id}-${requestParameters.table_index || 0}.xlsx`;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    return {blob, filename};
  }

  /**
   * Export governance action to Excel
   * Returns a blob with the Excel file and filename from Content-Disposition header
   *
   * IMPORTANT: This method bypasses the normal API middleware to preserve binary data integrity.
   * The middleware tries to parse responses as JSON which corrupts binary Excel files.
   */
  async publicExportGovernanceAction(requestParameters: {
    session_id: string;
    turn_id: string;
    preview_id: string;
  }): Promise<{blob: Blob; filename: string}> {
    // Build URL using the routes configuration (handles BASE_URL and proper encoding)
    const url = routes.aiAssistant.exportGovernanceAction({
      sessionId: requestParameters.session_id,
      turnId: requestParameters.turn_id,
      previewId: requestParameters.preview_id,
    });

    // Make raw fetch call to avoid middleware that corrupts binary data
    // credentials: 'include' ensures session cookies are sent
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        // Don't set Content-Type for binary responses
        // The browser will handle it correctly
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Export failed: ${response.status} ${errorText}`);
    }

    // Get the blob directly - this preserves binary data integrity
    const blob = await response.blob();

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `governance-action-${requestParameters.preview_id}.xlsx`;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    return {blob, filename};
  }

  /**
   * Skip a call
   */
  skip(_params?: any) {
    return null;
  }
}
